import { useApolloClient } from '@apollo/client';
import { GraphQLError } from 'graphql';

import {
	useErrorBoundary,
	useFeature,
	useMixpanel,
	useViewer,
	useWorkspace,
} from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

type FeatureConditions = NonNullable<
	graphql.FeaturesQuery['workspace']['features'][number]['requiredConditions']
>;

export function useAIAssistanceApproval(): {
	needsApproval: boolean;
	requiredConditions?: FeatureConditions;
	approve: () => void;
	approving: boolean;
	error?: GraphQLError;
	loading: boolean;
} {
	const workspace = useWorkspace();
	const mixpanel = useMixpanel();
	const viewer = useViewer();
	const client = useApolloClient();

	const featureEnabled = useFeature(graphql.FeatureName.ConversationAiInsights);

	const { data, loading, refetch } = graphql.useFeaturesQuery({
		variables: {
			id: workspace.id,
		},
	});

	const [approveFeatureForViewer, { loading: approving }] =
		graphql.useApproveFeatureForViewerMutation();

	const [error, attempt] = useErrorBoundary();

	const feature = data?.workspace.features.find(
		(feature) => feature.feature.id === graphql.FeatureName.ConversationAiInsights,
	);

	const requiredConditions = feature?.requiredConditions ?? undefined;

	// `needsViewerApproval` is true whenever no approval record exists, even for
	// workspaces without the feature, so the enabled check has to be explicit.
	const needsApproval = featureEnabled && !!feature?.needsViewerApproval && !!requiredConditions;

	const approve = attempt(async () => {
		if (!requiredConditions) {
			return;
		}

		const result = await approveFeatureForViewer({
			variables: {
				input: {
					featureID: graphql.FeatureName.ConversationAiInsights,
					conditionsVersion: requiredConditions.currentVersion,
				},
			},
		});

		if (result.data?.approveFeatureForViewer.approval) {
			await refetch();

			// The same approval backs User.conversationAiInsightsApproved, which the
			// chat reads from a separate query that this mutation cannot invalidate.
			if (viewer?.id) {
				client.cache.modify({
					id: client.cache.identify({ __typename: 'User', id: viewer.id }),
					fields: {
						conversationAiInsightsApproved: () => true,
					},
				});
			}

			mixpanel?.track('feature.approved', {
				featureName: graphql.FeatureName.ConversationAiInsights,
				targetType: graphql.FeatureApprovalTargetType.User,
			});
		}
	});

	return { needsApproval, requiredConditions, approve, approving, error, loading };
}
